import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ChevronRight, Menu, X, BookOpen, Layers, Code2, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import Navbar from '@/components/Navbar';
// @ts-ignore
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// @ts-ignore
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface DocSection {
  title: string;
  items: {
    title: string;
    href: string;
    icon?: React.ReactNode;
  }[];
}

const DOCS_NAVIGATION: DocSection[] = [
  {
    title: 'Introduction',
    items: [
      { title: 'What is SigLLM?', href: '/docs/introduction/what-is-sigllm', icon: <BookOpen className="w-4 h-4" /> },
      { title: 'Key Features', href: '/docs/introduction/key-features', icon: <Layers className="w-4 h-4" /> },
      { title: 'Architecture', href: '/docs/introduction/architecture', icon: <Layers className="w-4 h-4" /> },
    ],
  },
  {
    title: 'Integration',
    items: [
      { title: 'OpenAI SDK', href: '/docs/integration/openai-sdk', icon: <Code2 className="w-4 h-4" /> },
    ],
  },
];

const DocsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Determine current path, defaulting to first doc if root /docs is accessed
  useEffect(() => {
    if (location.pathname === '/docs' || location.pathname === '/docs/') {
      navigate(DOCS_NAVIGATION[0].items[0].href, { replace: true });
    }
  }, [location.pathname, navigate]);

  // Fetch Markdown content
  useEffect(() => {
    const fetchDoc = async () => {
      setIsLoading(true);
      try {
        // Remove /docs prefix to get the file path
        // Remove /docs prefix to get the file path
        // e.g. /docs/introduction/what-is-sigllm -> /introduction/what-is-sigllm
        const path = location.pathname.replace('/docs', '');
        if (!path || path === '/') return;

        const response = await fetch(`/docs${path}.md`);
        if (!response.ok) {
          throw new Error('Doc not found');
        }
        const text = await response.text();
        setContent(text);
      } catch (error) {
        console.error('Failed to load doc:', error);
        setContent('# 404: Not Found\n\nThe requested documentation page could not be found.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDoc();
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background flex flex-col pt-16">
      <Navbar />
      {/* Mobile Header for Docs Sidebar Toggle - Only visible on mobile, Navbar is fixed above */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b bg-background/95 backdrop-blur sticky top-16 z-40">
        <span className="font-bold text-lg">Menu</span>
        <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X /> : <Menu />}
        </Button>
      </div>

      <div className="container mx-auto flex-1 flex md:gap-8 px-4 max-w-7xl">
        {/* Sidebar Navigation */}
        <aside
          className={cn(
            "fixed inset-0 top-[124px] lg:top-[64px] z-30 w-full bg-background p-6 transition-transform lg:static lg:block lg:w-64 lg:transform-none lg:border-r lg:py-12",
            isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <nav className="space-y-8">
            {DOCS_NAVIGATION.map((section) => (
              <div key={section.title}>
                <h3 className="mb-3 text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  {section.title}
                </h3>
                <ul className="space-y-2">
                  {section.items.map((item) => (
                    <li key={item.href}>
                      <NavLink
                        to={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={({ isActive }) =>
                          cn(
                            "group flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-emerald-950/30 dark:hover:text-emerald-400",
                            isActive
                              ? "bg-emerald-100/50 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400"
                              : "text-slate-600 dark:text-slate-400"
                          )
                        }
                      >
                        {item.icon}
                        {item.title}
                        {/* {location.pathname === item.href && (
                          <ChevronRight className="ml-auto h-4 w-4 opacity-50" />
                        )} */}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 py-12 lg:pl-8 lg:pr-12 min-w-0">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent"></div>
            </div>
          ) : (
            <div className="prose prose-slate dark:prose-invert max-w-none prose-headings:font-bold prose-h1:text-4xl prose-h1:tracking-tight prose-a:text-emerald-600 prose-code:text-emerald-600 dark:prose-code:text-emerald-400 prose-pre:bg-slate-900 prose-pre:border prose-pre:border-white/10">
              <ReactMarkdown
                children={content}
                components={{
                  code({ node, inline, className, children, ...props }: any) {
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline && match ? (
                      <SyntaxHighlighter
                        style={vscDarkPlus}
                        language={match[1]}
                        PreTag="div"
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  }
                }}
              />
            </div>
          )}
        </main>

        {/* Right Sidebar (Table of Contents placeholder - optional, purely visual for now 
            since parsing headers from raw markdown for a real TOC is complex without plugins) */}
        <div className="hidden xl:block w-64 py-12 sticky top-20 h-screen">
          <div className="text-sm border-l pl-4 border-slate-200 dark:border-slate-800">
            <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-4">On this page</h4>
            <ul className="space-y-3 text-slate-500 dark:text-slate-400">
              {/* Simplified static logic or we could write a regex to find headers */}
              {content.match(/^#{2,3}\s+(.+)$/gm)?.map((header) => {
                const title = header.replace(/^#{2,3}\s+/, '');
                // Simple anchor link generation (not perfect but functional visual)
                return (
                  <li key={title}>
                    <a href="#" className="hover:text-emerald-500 transition-colors block truncate">{title}</a>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DocsPage;
